// The one cart engine for every surface (product forms, cards, mini-cart,
// cart page, checkout add-ons). Shopify is the authority: quantities,
// totals and discounts come from Shopify's Ajax Cart API responses; this
// module never computes a payable amount.
//
// - Lines are identified by their line KEY for the request, and by their
//   IDENTITY (variant + selling plan + visible and underscore properties)
//   to find them again: Shopify may change a key (for example when a
//   discount applies), so a remembered key is re-resolved from the
//   canonical cart before it is used. The same variant with other
//   properties or another selling plan is another line. Two lines with one
//   identity (a split) are ambiguous: nothing is changed by guessing.
// - All mutations run through ONE serialized queue: no races, no double adds
//   and no late answer overwriting a newer state.
// - Every add is measured against a fresh canonical read inside the same
//   queued task: after the request (answered, refused or lost) the cart is
//   read again and the outcome is classified per requested item as
//   'added' | 'partial' | 'none' | 'unknown'. A 422 or a lost answer is
//   never assumed to mean "nothing was added", and a response quantity is
//   never taken as the added amount.
// - Typed quantities are kept as pending intent per line identity and
//   flushed after a short pause or on settle(); a newer value replaces an
//   older unsent one. settle() is a stable barrier: it repeats until no
//   intent and no request is left, and reports any intent Shopify did not
//   confirm.
// - After an error or an uncertain outcome the canonical cart (/cart.js) is
//   read before the next mutation; while it cannot be read, nothing mutates.
// - Sections are re-rendered with Shopify's bundled section rendering
//   (max 5 ids per request); a missing (null) section is reported.
//
// No DOM in this module: the transport (fetch) and timers are injected so
// the engine runs unchanged in offline tests with a mock transport.

export const MAX_SECTIONS = 5;
const SETTLE_ROUNDS = 6;

/** Stable identity of a line: variant, selling plan and properties (sorted). */
export function identityOf({ variant, sellingPlan = null, properties = {} }) {
  const props = Object.keys(properties || {})
    .filter((k) => properties[k] !== null && properties[k] !== undefined && properties[k] !== '')
    .sort()
    .map((k) => [k, String(properties[k])]);
  return JSON.stringify([String(variant), sellingPlan === null || sellingPlan === undefined ? null : String(sellingPlan), props]);
}

/**
 * L01/F01: how much of the measured delta of one add attempt is provably
 * OURS. The delta (post-read minus pre-read of that identity) may contain
 * units another writer (another tab, same properties) added or removed
 * meanwhile, so a delta is never proof of ownership on its own. Only
 * Shopify's 2xx answer to OUR request proves that the request was applied:
 *   confirmed and delta >= requested -> own = requested ('confirmed'); any
 *                                       surplus belongs to someone else
 *   everything else (refused, lost or unreadable answer, or units removed
 *   elsewhere after a confirmed add)    -> own share unknown
 * Returns { own: number | null, proof: 'confirmed' | null }.
 */
export function attribute({ requested, delta, confirmed }) {
  if (delta === null || delta === undefined) return { own: null, proof: null };
  return confirmed && delta >= requested ? { own: requested, proof: 'confirmed' } : { own: null, proof: null };
}

function normalize(raw) {
  return {
    token: raw.token || null,
    count: raw.item_count,
    total: raw.total_price,
    originalTotal: raw.original_total_price,
    totalDiscount: raw.total_discount,
    currency: raw.currency,
    lines: (raw.items || []).map((i) => {
      const line = {
        key: i.key,
        variant: i.variant_id ?? i.id,
        product: i.product_id,
        quantity: i.quantity,
        finalLinePrice: i.final_line_price,
        properties: i.properties || {},
        sellingPlan: (i.selling_plan_allocation && i.selling_plan_allocation.selling_plan && i.selling_plan_allocation.selling_plan.id) ?? null,
        sku: i.sku || null
      };
      line.identity = identityOf(line);
      return line;
    })
  };
}

const itemIdentity = (item) => identityOf({ variant: item.id, sellingPlan: item.selling_plan ?? null, properties: item.properties || {} });
const quantityOf = (c, identity) => (c ? c.lines.filter((l) => l.identity === identity).reduce((n, l) => n + l.quantity, 0) : 0);

export function createCart(options) {
  const {
    fetch: transport,
    routes,
    sections = () => [],
    sectionsUrl = () => '/',
    debounce = 300,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (id) => clearTimeout(id)
  } = options;

  let cart = null;
  let revision = 0;
  let uncertain = false;
  let inflight = 0;
  let timer = null;
  let chain = Promise.resolve();
  const pending = new Map();          // identity -> { quantity, key }
  // results of customer quantity/remove intents that Shopify refused,
  // adjusted or left unknown; they block the checkout barrier until the
  // customer corrects the line or explicitly acknowledges the shown cart
  const issues = new Map();           // identity | key -> { requested, actual, outcome }
  const subscribers = new Set();

  const url = (route, suffix = '.js') => route.replace(/\/$/, '') + suffix;

  function emit(type, detail = {}) {
    for (const fn of subscribers) {
      try { fn({ type, ...detail }); } catch { /* a subscriber never breaks the engine */ }
    }
  }

  function setCanonical(raw) {
    cart = normalize(raw);
    revision += 1;
    uncertain = false;
    emit('cart', { cart, revision });
  }

  async function request(target, body) {
    let response;
    try {
      response = await transport(target, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'same-origin'
      });
    } catch {
      return { ok: false, status: 0, network: true, error: { message: 'network' } };
    }
    let data = null;
    try { data = await response.json(); } catch { data = null; }
    if (!response.ok) {
      const error = data && (data.description || data.message)
        ? { message: data.message || '', description: data.description || '' }
        : { message: 'http_' + response.status };
      return { ok: false, status: response.status, error };
    }
    if (data === null) return { ok: false, status: response.status, invalid: true, error: { message: 'invalid_response' } };
    return { ok: true, status: response.status, data };
  }

  function enqueue(task) {
    const run = chain.then(async () => {
      inflight += 1;
      emit('busy', { busy: true });
      try { return await task(); } finally {
        inflight -= 1;
        if (!inflight) emit('busy', { busy: false });
      }
    });
    chain = run.catch(() => {});
    return run;
  }

  async function refetch() {
    const r = await request(url(routes.cart));
    if (r.ok && r.data && Array.isArray(r.data.items)) {
      setCanonical(r.data);
      return { ok: true };
    }
    uncertain = true;
    emit('uncertain', {});
    return { ok: false };
  }

  async function ensureCanonical() {
    if (cart && !uncertain) return { ok: true };
    return refetch();
  }

  function sectionRequest() {
    const ids = [...new Set(sections().filter(Boolean))].slice(0, MAX_SECTIONS);
    return ids.length ? { sections: ids, sections_url: sectionsUrl() } : {};
  }

  function missing(requested, rendered) {
    return (requested.sections || []).filter((id) => !rendered || rendered[id] == null);
  }

  /**
   * Re-resolve a remembered line { key, identity } in the canonical cart.
   * whole: the caller means ALL units of that identity (an upgrade source);
   * then a split over several lines is ambiguous even if the key exists.
   */
  function resolve(target, whole = false) {
    if (!cart) return { status: 'unknown' };
    const byKey = cart.lines.find((l) => l.key === target.key);
    if (byKey && whole && cart.lines.filter((l) => l.identity === byKey.identity).length > 1) return { status: 'ambiguous' };
    if (byKey && (!target.identity || byKey.identity === target.identity)) return { status: 'found', line: byKey };
    if (!target.identity) return { status: 'gone' };
    const same = cart.lines.filter((l) => l.identity === target.identity);
    if (same.length === 1) return { status: 'found', line: same[0] };
    if (same.length > 1) return { status: 'ambiguous', lines: same };
    return { status: 'gone' };
  }

  /**
   * Add items: [{ id, quantity, properties?, selling_plan? }]. The result is
   * measured against the canonical cart, per item:
   *   outcome 'added'   every requested unit is in the cart (also when the
   *                     answer was lost or Shopify answered with an error)
   *           'partial' some, not all
   *           'none'    nothing was added
   *           'unknown' the request WAS sent but the cart could not be read
   *                     afterwards (the baseline is proven, the result not)
   * and two outcomes in which NOTHING was sent (sent: false):
   *           'not_sent'            the fresh pre-read failed: no baseline
   *           'precondition_failed' meta.precondition(cart) refused the
   *                                 fresh canonical state (e.g. the source
   *                                 of an upgrade changed elsewhere)
   * baseline is 'proven' only when the pre-read succeeded in this task.
   * Per item, added is the observed canonical delta and own/proof the share
   * that is provably this request's (attribute(), L01/F01); only own may
   * ever be taken back, and only a confirmed own add counts as ours.
   */
  function add(items, meta = {}) {
    return enqueue(async () => {
      // always a fresh canonical read first: the baseline must include lines
      // added elsewhere (another tab), so they are never counted as ours
      const pre = await refetch();
      if (!pre.ok) return { ok: false, sent: false, baseline: null, outcome: 'not_sent', error: { message: 'cart_unavailable' }, items: [] };
      const baseline = items.map((item) => ({ identity: itemIdentity(item), before: quantityOf(cart, itemIdentity(item)), requested: item.quantity }));
      if (meta.precondition) {
        const verdict = meta.precondition(cart);
        if (verdict !== true) {
          return { ok: false, sent: false, baseline: 'proven', outcome: 'precondition_failed', reason: typeof verdict === 'string' ? verdict : 'changed', items: baseline.map((b) => ({ ...b, added: 0, outcome: 'none' })) };
        }
      }
      const req = sectionRequest();
      const r = await request(url(routes.add), { items, ...req });
      const canonical = await refetch();
      const measured = baseline.map((b) => {
        if (!canonical.ok) return { ...b, added: null, outcome: 'unknown', own: null, proof: null };
        const added = Math.max(0, quantityOf(cart, b.identity) - b.before);
        const outcome = added >= b.requested ? 'added' : added > 0 ? 'partial' : 'none';
        // added = the observed canonical delta; own = the provable own share
        return { ...b, added, outcome, ...attribute({ requested: b.requested, delta: added, confirmed: r.ok }) };
      });
      const outcome = measured.some((m) => m.outcome === 'unknown') ? 'unknown'
        : measured.every((m) => m.outcome === 'added') ? 'added'
          : measured.every((m) => m.outcome === 'none') ? 'none' : 'partial';
      const rendered = r.ok ? r.data.sections || null : null;
      if (r.ok) emit('render', { sections: rendered, missing: missing(req, rendered), action: 'add', meta });
      else emit('render', { sections: null, missing: req.sections || [], action: 'add', meta });
      if (outcome !== 'added') emit('error', { action: 'add', outcome, status: r.status, error: r.error || null, meta });
      const keys = canonical.ok ? measured.flatMap((m) => cart.lines.filter((l) => l.identity === m.identity).map((l) => l.key)) : [];
      return {
        ok: outcome === 'added',
        sent: true,
        baseline: 'proven',
        outcome,
        items: measured,
        keys,
        status: r.status,
        answered: r.ok,
        error: r.error || null,
        missing: r.ok ? missing(req, rendered) : req.sections || []
      };
    });
  }

  /**
   * Change one remembered line ({ key, identity } or a key) to an exact
   * quantity; 0 removes it. quantity may also be a decision function
   * decide({ total, lines, cart }) -> number | { refuse: reason }, evaluated
   * on a FRESH canonical read inside the same queued task (meta.fresh is
   * then implied): it re-confirms quantities right before the request, so a
   * line changed elsewhere is never removed or reduced on old information.
   * A refusal sends nothing (outcome 'precondition_failed').
   */
  function change(target, quantity, meta = {}) {
    return enqueue(() => sendChange(typeof target === 'string' ? { key: target } : target, quantity, meta));
  }

  async function sendChange(target, wanted, meta) {
    const decide = typeof wanted === 'function' ? wanted : null;
    const pre = decide || meta.fresh ? await refetch() : await ensureCanonical();
    if (!pre.ok) return { ok: false, sent: false, outcome: 'not_sent', error: { message: 'cart_unavailable' } };
    let quantity = wanted;
    if (decide) {
      const lines = target.identity ? cart.lines.filter((l) => l.identity === target.identity) : cart.lines.filter((l) => l.key === target.key);
      const verdict = decide({ total: lines.reduce((n, l) => n + l.quantity, 0), lines, cart });
      if (typeof verdict !== 'number') {
        const reason = (verdict && verdict.refuse) || 'changed';
        emit('error', { action: 'change', outcome: 'precondition_failed', reason, meta });
        return { ok: false, sent: false, outcome: 'precondition_failed', reason };
      }
      quantity = verdict;
    }
    let found = resolve(target, meta.whole);
    const req = sectionRequest();
    let r = null;
    let identity = null;
    // An ABSOLUTE quantity may be sent a second time when the canonical cart
    // shows that only the key changed (sending it again cannot add or remove
    // more). A DECIDED quantity (remove/undo based on the state of that
    // moment) gets exactly ONE attempt: after a failure the state may have
    // changed, so a new attempt needs a new decision by the caller (R01).
    const attempts = decide ? 1 : 2;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (found.status === 'ambiguous') {
        emit('error', { action: 'change', outcome: 'ambiguous', error: { message: 'ambiguous_line' }, meta });
        return { ok: false, outcome: 'ambiguous', error: { message: 'ambiguous_line' } };
      }
      if (found.status === 'gone') {
        // removing a line that is no longer there is already done; any other
        // change to a vanished line is refused, never redirected to another line
        if (quantity === 0) return { ok: true, outcome: 'already_removed', requested: 0, actual: 0 };
        emit('error', { action: 'change', outcome: 'line_gone', error: { message: 'line_gone' }, meta });
        return { ok: false, outcome: 'line_gone', error: { message: 'line_gone' } };
      }
      const usedKey = found.line.key;
      identity = found.line.identity;
      r = await request(url(routes.change), { id: usedKey, quantity, ...req });
      if (r.ok && Array.isArray(r.data.items)) { setCanonical(r.data); break; }
      const canonical = await refetch();
      if (!canonical.ok) {
        emit('error', { action: 'change', outcome: 'unknown', status: r.status, error: r.error, meta });
        return { ok: false, outcome: 'unknown', applied: null, status: r.status, error: r.error };
      }
      if (quantityOf(cart, identity) === quantity && !(meta.whole && quantity === 0 && cart.lines.some((l) => l.identity === identity))) break;
      if (decide) {
        // Shopify answered with a refusal (4xx): provably not applied.
        // No answer (network, 5xx, invalid JSON) and the goal is not visible:
        // applied or not is unknown; the caller must not subtract again.
        const refused = r.status >= 400 && r.status < 500;
        const outcome = refused ? 'not_applied' : 'unknown';
        emit('error', { action: 'change', outcome, status: r.status, error: r.error, meta });
        return { ok: false, outcome, applied: refused ? false : null, status: r.status, error: r.error || { message: 'invalid_response' }, requested: quantity, actual: quantityOf(cart, identity) };
      }
      found = resolve({ identity: target.identity || identity }, meta.whole);
      if (found.status !== 'found' || found.line.key === usedKey) {
        if (found.status === 'found') break;          // same key: a real refusal, no second try
        if (found.status === 'gone' && quantity === 0) return { ok: true, outcome: 'changed_unanswered', requested: 0, actual: 0 };
        continue;                                      // gone/ambiguous: reported at the top of the loop
      }
    }
    const actual = quantityOf(cart, identity);
    const answered = r.ok && Array.isArray(r.data.items);
    const rendered = answered ? r.data.sections || null : null;
    emit('render', { sections: rendered, missing: answered ? missing(req, rendered) : req.sections || [], action: 'change', meta });
    if (actual === quantity) {
      // reached: also when the answer was lost but Shopify applied it
      return { ok: true, outcome: answered ? 'changed' : 'changed_unanswered', requested: quantity, actual, missing: answered ? missing(req, rendered) : [] };
    }
    if (answered) {
      emit('adjusted', { key: target.key, requested: quantity, actual });
      return { ok: true, adjusted: true, outcome: 'adjusted', requested: quantity, actual };
    }
    emit('error', { action: 'change', outcome: 'not_applied', status: r.status, error: r.error, meta });
    return { ok: false, outcome: 'not_applied', status: r.status, error: r.error || { message: 'invalid_response' }, requested: quantity, actual };
  }

  function recordIntent(id, r) {
    if (!r.ok || r.adjusted) issues.set(id, { requested: r.requested, actual: r.actual, outcome: r.outcome });
    else issues.delete(id);
  }

  function remove(target, meta = {}) {
    const t = typeof target === 'string' ? { key: target } : target;
    for (const [id, p] of pending) if (p.key === t.key || id === t.identity) pending.delete(id);
    if (meta.source !== 'remove') return change(t, 0, meta);
    const line = cart && cart.lines.find((l) => l.key === t.key);
    const id = t.identity || (line ? line.identity : 'key:' + t.key);
    return change(t, 0, meta).then((r) => { recordIntent(id, r); return r; });
  }

  /** Record typed intent; it is sent after a pause or on flush/settle. */
  function setQuantity(key, quantity) {
    const line = cart && cart.lines.find((l) => l.key === key);
    const id = line ? line.identity : 'key:' + key;
    pending.set(id, { key, identity: line ? line.identity : null, quantity });
    emit('pending', { key, quantity });
    if (timer !== null) clearTimer(timer);
    timer = setTimer(() => { timer = null; flush(); }, debounce);
  }

  function flush() {
    if (timer !== null) { clearTimer(timer); timer = null; }
    const ids = [...pending.keys()];
    return Promise.all(ids.map((id) => enqueue(async () => {
      if (!pending.has(id)) return { ok: true, skipped: true };
      const intent = pending.get(id);
      pending.delete(id);
      const target = { key: intent.key, identity: intent.identity };
      const found = cart && !uncertain ? resolve(target) : null;
      if (found && found.status === 'found' && found.line.quantity === intent.quantity) return { ok: true, skipped: true };
      const r = await sendChange(target, intent.quantity, { source: 'quantity' });
      recordIntent(intent.identity || 'key:' + intent.key, r);
      return { ...r, intent: intent.quantity };
    })));
  }

  /**
   * The quantity/checkout barrier. Repeats flush + wait until no typed
   * intent and no request is left (input that arrives while a request is in
   * flight is included), then confirms the canonical cart. ok is false when
   * any intent failed, was adjusted by Shopify, the cart is uncertain, or
   * input kept arriving for SETTLE_ROUNDS rounds (reason 'unsettled').
   */
  async function settle() {
    const results = [];
    let rounds = 0;
    do {
      results.push(...(await flush()));
      await chain;
      rounds += 1;
    } while ((pending.size || timer !== null) && rounds < SETTLE_ROUNDS);
    if (uncertain || !cart) await enqueue(() => refetch());
    const unsettled = pending.size > 0 || timer !== null;
    // every intent counts, also flushes that were already running before
    // this barrier started (a debounce, onChange or Enter flush): their
    // outcome is in `issues`, not in this call's own results
    const failed = results.filter((r) => !r.ok || r.adjusted);
    const open = [...issues.values()];
    const ok = !failed.length && !open.length && !uncertain && !!cart && !unsettled;
    const reason = ok ? null : unsettled ? 'unsettled' : uncertain || !cart ? 'uncertain'
      : [...failed, ...open].some((r) => r.adjusted || r.outcome === 'adjusted') ? 'adjusted' : 'failed';
    return { ok, cart, results, issues: open, reason };
  }

  return {
    identityOf,
    add,
    change,
    remove,
    setQuantity,
    flush,
    settle,
    attribute,
    resolve: (target, whole = false) => resolve(typeof target === 'string' ? { key: target } : target, whole),
    refetch: () => enqueue(() => refetch()),
    /** The customer has seen the canonical cart after a refused/adjusted intent. */
    acknowledge() { issues.clear(); },
    issues: () => [...issues.values()],
    seed(raw) {
      if (raw && Array.isArray(raw.items)) setCanonical(raw);
    },
    view() {
      return {
        cart,
        revision,
        uncertain,
        busy: inflight > 0,
        pending: Object.fromEntries([...pending.values()].map((p) => [p.key, p.quantity]))
      };
    },
    subscribe(fn) {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    }
  };
}
